/**
 * DBDL — Design Your Own logo uploader
 * Optional modal customizer: upload a logo, then freely drag/resize it
 * directly on the product image. Does not gate Add to Cart — customizing
 * is entirely opt-in.
 */
(function () {
  'use strict';

  function initUploader(cfg) {
    const openBtn = document.getElementById('DyoOpenBtn-' + cfg.blockId);
    const openBtnLabel = document.getElementById('DyoOpenBtnLabel-' + cfg.blockId);
    const modal = document.getElementById('DyoModal-' + cfg.blockId);
    const closeBtn = document.getElementById('DyoCloseBtn-' + cfg.blockId);
    const doneBtn = document.getElementById('DyoDoneBtn-' + cfg.blockId);
    const canvas = document.getElementById('DyoCanvas-' + cfg.blockId);
    if (!modal || !canvas) return;
    const ctx = canvas.getContext('2d');

    const uploadBtn = document.getElementById('DyoUploadBtn-' + cfg.blockId);
    const uploadIcon = document.getElementById('DyoUploadIcon-' + cfg.blockId);
    const uploadLabel = document.getElementById('DyoUploadLabel-' + cfg.blockId);
    const fileInput = document.getElementById('DyoFileInput-' + cfg.blockId);
    const errorEl = document.getElementById('DyoError-' + cfg.blockId);
    const logoUrlInput = document.getElementById('DyoLogoUrl-' + cfg.blockId);
    const previewUrlInput = document.getElementById('DyoPreviewUrl-' + cfg.blockId);
    const logoPositionInput = document.getElementById('DyoLogoPosition-' + cfg.blockId);
    const decorationTypeInput = document.getElementById('DyoDecorationType-' + cfg.blockId);
    const decorationRadios = document.querySelectorAll('input[name="DyoDecoration-' + cfg.blockId + '"]');
    const feeNoteEl = document.getElementById('DyoFeeNote-' + cfg.blockId);

    const CW = canvas.width;
    const CH = canvas.height;
    const HANDLE_PX = 12;
    const HANDLE_HIT = 24;
    const MIN_LOGO_PX = 32;

    const PZ = {
      x: Math.round(cfg.printZone.xPct * CW),
      y: Math.round(cfg.printZone.yPct * CH),
      w: Math.round(cfg.printZone.wPct * CW),
      h: Math.round(cfg.printZone.hPct * CH),
    };

    let productImg = null;
    let logoImg = null;
    let logoUrl = null;
    let hasLogo = false;
    let previewUploading = false;
    let previewDebounce = null;
    let decorationType = 'heat_transfer';

    const DECORATION_LABELS = { heat_transfer: 'Heat Transfer', embroidery: 'Embroidery' };

    function feeVariantIdForCurrentDecoration() {
      const raw = decorationType === 'embroidery' ? cfg.embroideryFeeVariantId : cfg.heatTransferFeeVariantId;
      const id = parseInt(raw, 10);
      return Number.isFinite(id) && id > 0 ? id : null;
    }

    const logo = { x: PZ.x, y: PZ.y, w: 0, h: 0, ar: 1 };

    const ui = {
      selected: false,
      dragging: false,
      resizing: false,
      handle: null,
      origin: { mx: 0, my: 0, lx: 0, ly: 0, lw: 0, lh: 0 },
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    function render() {
      ctx.clearRect(0, 0, CW, CH);

      if (productImg) {
        ctx.drawImage(productImg, 0, 0, CW, CH);
      } else {
        ctx.fillStyle = '#f3f3f3';
        ctx.fillRect(0, 0, CW, CH);
      }

      ctx.save();
      ctx.beginPath();
      ctx.rect(PZ.x, PZ.y, PZ.w, PZ.h);
      ctx.strokeStyle = 'rgba(18,18,18,0.5)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([7, 5]);
      ctx.stroke();
      ctx.restore();

      if (!logoImg) {
        ctx.save();
        ctx.font = '500 16px sans-serif';
        ctx.fillStyle = 'rgba(18,18,18,0.55)';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('Your logo will appear here', PZ.x + PZ.w / 2, PZ.y + PZ.h / 2 - 12);
        ctx.font = '400 14px sans-serif';
        ctx.fillText('Upload a file to get started', PZ.x + PZ.w / 2, PZ.y + PZ.h / 2 + 14);
        ctx.restore();
        return;
      }

      ctx.drawImage(logoImg, logo.x, logo.y, logo.w, logo.h);

      if (ui.selected) drawHandles();
    }

    function drawHandles() {
      const handles = getHandles();

      ctx.save();
      ctx.strokeStyle = 'rgb(18,18,18)';
      ctx.lineWidth = 1;
      ctx.setLineDash([]);
      ctx.strokeRect(logo.x, logo.y, logo.w, logo.h);

      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = 'rgb(18,18,18)';
      ctx.lineWidth = 1.5;

      Object.values(handles).forEach(({ hx, hy }) => {
        const hs = HANDLE_PX;
        ctx.fillRect(hx - hs / 2, hy - hs / 2, hs, hs);
        ctx.strokeRect(hx - hs / 2, hy - hs / 2, hs, hs);
      });

      ctx.restore();
    }

    function getHandles() {
      return {
        nw: { hx: logo.x, hy: logo.y },
        ne: { hx: logo.x + logo.w, hy: logo.y },
        sw: { hx: logo.x, hy: logo.y + logo.h },
        se: { hx: logo.x + logo.w, hy: logo.y + logo.h },
      };
    }

    // ─── Coordinate helpers ─────────────────────────────────────────────────────
    function canvasXY(e) {
      const rect = canvas.getBoundingClientRect();
      const scaleX = CW / rect.width;
      const scaleY = CH / rect.height;
      const src = e.touches ? e.touches[0] : e;
      return {
        x: (src.clientX - rect.left) * scaleX,
        y: (src.clientY - rect.top) * scaleY,
      };
    }

    function hitHandle(cx, cy) {
      if (!ui.selected) return null;
      const handles = getHandles();
      for (const [name, { hx, hy }] of Object.entries(handles)) {
        if (Math.abs(cx - hx) <= HANDLE_HIT / 2 && Math.abs(cy - hy) <= HANDLE_HIT / 2) return name;
      }
      return null;
    }

    function hitLogo(cx, cy) {
      return cx >= logo.x && cx <= logo.x + logo.w && cy >= logo.y && cy <= logo.y + logo.h;
    }

    function clamp() {
      logo.w = Math.max(MIN_LOGO_PX, Math.min(PZ.w, logo.w));
      logo.h = Math.max(MIN_LOGO_PX, Math.min(PZ.h, logo.h));
      logo.x = Math.max(PZ.x, Math.min(PZ.x + PZ.w - logo.w, logo.x));
      logo.y = Math.max(PZ.y, Math.min(PZ.y + PZ.h - logo.h, logo.y));
    }

    // ─── Pointer events (drag + corner-resize) ─────────────────────────────────
    function onDown(e) {
      if (!logoImg) return;
      e.preventDefault();

      const { x, y } = canvasXY(e);
      const handle = hitHandle(x, y);

      if (handle) {
        ui.resizing = true;
        ui.handle = handle;
        ui.origin = { mx: x, my: y, lx: logo.x, ly: logo.y, lw: logo.w, lh: logo.h };
        return;
      }

      if (hitLogo(x, y)) {
        ui.dragging = true;
        ui.selected = true;
        ui.origin = { mx: x, my: y, lx: logo.x, ly: logo.y };
        render();
        return;
      }

      ui.selected = false;
      render();
    }

    function onMove(e) {
      if (!ui.dragging && !ui.resizing) {
        if (logoImg) {
          const { x, y } = canvasXY(e);
          const h = hitHandle(x, y);
          const cursors = { nw: 'nw-resize', ne: 'ne-resize', sw: 'sw-resize', se: 'se-resize' };
          canvas.style.cursor = h ? cursors[h] : hitLogo(x, y) ? 'move' : 'default';
        }
        return;
      }
      e.preventDefault();

      const { x, y } = canvasXY(e);
      const dx = x - ui.origin.mx;
      const dy = y - ui.origin.my;

      if (ui.dragging) {
        logo.x = ui.origin.lx + dx;
        logo.y = ui.origin.ly + dy;
      }

      if (ui.resizing) {
        const orig = ui.origin;
        const ar = logo.ar;

        switch (ui.handle) {
          case 'se': {
            logo.w = Math.max(MIN_LOGO_PX, orig.lw + dx);
            logo.h = logo.w / ar;
            break;
          }
          case 'sw': {
            const nw = Math.max(MIN_LOGO_PX, orig.lw - dx);
            logo.x = orig.lx + (orig.lw - nw);
            logo.w = nw;
            logo.h = nw / ar;
            break;
          }
          case 'ne': {
            const nw = Math.max(MIN_LOGO_PX, orig.lw + dx);
            logo.y = orig.ly + (orig.lh - nw / ar);
            logo.w = nw;
            logo.h = nw / ar;
            break;
          }
          case 'nw': {
            const nw = Math.max(MIN_LOGO_PX, orig.lw - dx);
            logo.x = orig.lx + (orig.lw - nw);
            logo.y = orig.ly + (orig.lh - nw / ar);
            logo.w = nw;
            logo.h = nw / ar;
            break;
          }
        }
      }

      clamp();
      render();
      schedulePositionUpdate();
    }

    function onUp() {
      ui.dragging = false;
      ui.resizing = false;
      ui.handle = null;
      schedulePositionUpdate();
    }

    // ─── Position property (debounced) ─────────────────────────────────────────
    function schedulePositionUpdate() {
      clearTimeout(previewDebounce);
      previewDebounce = setTimeout(() => {
        storePositionProperty();
        if (logoUrl) schedulePreviewUpload();
      }, 500);
    }

    function storePositionProperty() {
      if (!logoImg) return;
      const relX = ((logo.x - PZ.x) / PZ.w * 100).toFixed(1);
      const relY = ((logo.y - PZ.y) / PZ.h * 100).toFixed(1);
      const relW = (logo.w / PZ.w * 100).toFixed(1);
      const relH = (logo.h / PZ.h * 100).toFixed(1);
      if (logoPositionInput) logoPositionInput.value = `x:${relX}%,y:${relY}%,w:${relW}%,h:${relH}%`;
    }

    // ─── File handling ──────────────────────────────────────────────────────────
    function onFileSelect(e) {
      const file = e.target.files[0];
      if (!file) return;
      e.target.value = '';

      const ALLOWED = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
      if (!ALLOWED.includes(file.type)) {
        showError('Please upload a PNG, JPG, SVG, or WebP image.');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        showError('File must be under 10 MB. Please compress your image and try again.');
        return;
      }

      hideError();

      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          logoImg = img;
          logo.ar = img.width / img.height;
          placeLogoInitial();
          ui.selected = true;
          render();
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);

      uploadToCloudinary(file);
    }

    function placeLogoInitial() {
      const targetW = PZ.w * 0.55;
      const targetH = targetW / logo.ar;
      const finalH = Math.min(targetH, PZ.h * 0.55);
      const finalW = finalH * logo.ar;

      logo.w = finalW;
      logo.h = finalH;
      logo.x = PZ.x + (PZ.w - finalW) / 2;
      logo.y = PZ.y + (PZ.h - finalH) / 2;
    }

    async function uploadToCloudinary(file) {
      if (!cfg.cloudName || !cfg.uploadPreset) {
        console.warn('[DYO] Cloudinary cloud name or upload preset not configured.');
        showError('Upload service not configured. Please contact the store.');
        return;
      }

      setUploadUI('uploading');

      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('upload_preset', cfg.uploadPreset);
        fd.append('folder', 'dbdl/logos');
        fd.append('tags', 'customer-logo,dyo');

        const res = await fetch('https://api.cloudinary.com/v1_1/' + cfg.cloudName + '/image/upload', {
          method: 'POST',
          body: fd,
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error((errData.error && errData.error.message) || 'Upload failed (' + res.status + ')');
        }

        const data = await res.json();
        logoUrl = data.secure_url;
        if (logoUrlInput) logoUrlInput.value = logoUrl;

        setUploadUI('done');
        hasLogo = true;
        updateTriggerLabel();
        storePositionProperty();
        schedulePreviewUpload();
      } catch (err) {
        console.error('[DYO] Cloudinary upload error:', err);
        setUploadUI('error');
        showError('Logo upload failed. Please check your connection and try again.');
      }
    }

    function schedulePreviewUpload() {
      if (!logoUrl) return;
      clearTimeout(previewDebounce);
      previewDebounce = setTimeout(doPreviewUpload, 700);
    }

    async function doPreviewUpload() {
      if (!logoUrl || previewUploading) return;
      previewUploading = true;

      try {
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.88));
        const fd = new FormData();
        fd.append('file', blob, 'preview.jpg');
        fd.append('upload_preset', cfg.uploadPreset);
        fd.append('folder', 'dbdl/previews');
        fd.append('tags', 'customer-preview,dyo');

        const res = await fetch('https://api.cloudinary.com/v1_1/' + cfg.cloudName + '/image/upload', {
          method: 'POST',
          body: fd,
        });

        if (res.ok) {
          const data = await res.json();
          if (previewUrlInput) previewUrlInput.value = data.secure_url;
        }
      } catch (err) {
        console.warn('[DYO] Preview upload failed (non-critical):', err);
      } finally {
        previewUploading = false;
      }
    }

    // ─── Upload button UI states ────────────────────────────────────────────────
    const ICONS = {
      idle: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>',
      spinner: '<span class="dyo-uploader__spinner" aria-hidden="true"></span>',
      check: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>',
      error: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
    };

    function setUploadUI(state) {
      uploadBtn.disabled = state === 'uploading';
      uploadBtn.dataset.state = state;

      if (uploadIcon) {
        uploadIcon.innerHTML = ICONS[state === 'uploading' ? 'spinner' : state === 'done' ? 'check' : state === 'error' ? 'error' : 'idle'];
      }
      if (uploadLabel) {
        uploadLabel.textContent = { idle: 'Upload Your Logo', uploading: 'Uploading…', done: 'Change Logo', error: 'Try Again' }[state] || 'Upload Your Logo';
      }
    }

    function updateTriggerLabel() {
      if (openBtnLabel) openBtnLabel.textContent = hasLogo ? 'Edit Your Logo' : 'Design Your Own';
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
      setTimeout(() => {
        errorEl.hidden = true;
      }, 6000);
    }

    function hideError() {
      if (errorEl) errorEl.hidden = true;
    }

    function loadProductImage() {
      if (!cfg.productImage) {
        render();
        return;
      }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        productImg = img;
        render();
      };
      img.onerror = () => render();
      img.src = cfg.productImage;
    }

    // ─── Product image preview overlay ─────────────────────────────────────────
    // Shows the composited canvas (product + logo) directly on the main product
    // photo once the customer closes the modal, so it's clear what's being
    // ordered without having to reopen the customizer.
    let previewOverlay = null;

    function findFeaturedMediaEl() {
      const section = document.getElementById('MainProduct-' + cfg.sectionId);
      if (!section) return null;

      // Primary: Dawn's own id convention for the featured gallery slide
      // (see snippets/product-media-gallery.liquid — `Slide-{{ section.id }}-{{ featured_media.id }}`).
      if (cfg.featuredMediaId) {
        const slide = document.getElementById('Slide-' + cfg.sectionId + '-' + cfg.featuredMediaId);
        const media = slide && slide.querySelector('.media');
        if (media) return media;
      }

      // Fallback: the slide Dawn marks as active.
      return section.querySelector('.product__media-item.is-active .media');
    }

    function applyProductPreview() {
      if (!hasLogo) return;

      const mediaEl = findFeaturedMediaEl();
      if (!mediaEl) {
        console.warn('[DYO] Could not find the product gallery image to overlay the preview onto.');
        return;
      }

      try {
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);

        if (!previewOverlay) {
          previewOverlay = document.createElement('img');
          previewOverlay.className = 'dyo-product-preview';
          previewOverlay.alt = 'Your customized product preview';
          previewOverlay.addEventListener('click', openModal);
        }
        if (previewOverlay.parentElement !== mediaEl) mediaEl.appendChild(previewOverlay);
        previewOverlay.src = dataUrl;
      } catch (err) {
        console.error('[DYO] Could not render the product preview overlay:', err);
      }
    }

    // ─── Modal open/close ───────────────────────────────────────────────────────
    function openModal() {
      if (typeof modal.showModal === 'function') {
        modal.showModal();
      } else {
        modal.setAttribute('open', '');
      }
    }

    function closeModal() {
      if (typeof modal.close === 'function' && modal.open) {
        modal.close();
      } else {
        modal.removeAttribute('open');
      }
      applyProductPreview();
    }

    openBtn && openBtn.addEventListener('click', openModal);
    closeBtn && closeBtn.addEventListener('click', closeModal);
    doneBtn && doneBtn.addEventListener('click', closeModal);

    // Click on the backdrop (the <dialog> element itself, outside .dyo-modal__inner) closes it.
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    // ─── Decoration type ────────────────────────────────────────────────────────
    function updateFeeNote() {
      if (!feeNoteEl) return;
      const text = decorationType === 'embroidery' ? cfg.embroideryFeeText : cfg.heatTransferFeeText;
      feeNoteEl.textContent = text || '';
      feeNoteEl.hidden = !text;
    }

    function onDecorationChange(e) {
      decorationType = e.target.value === 'embroidery' ? 'embroidery' : 'heat_transfer';
      if (decorationTypeInput) decorationTypeInput.value = DECORATION_LABELS[decorationType];
      updateFeeNote();
    }

    decorationRadios.forEach((radio) => radio.addEventListener('change', onDecorationChange));
    updateFeeNote();

    // ─── Decoration fee: add a second cart line item alongside the product ────
    // Only intercepts the product form's submit when a logo has been uploaded
    // AND a fee variant is configured for the selected decoration type — plain
    // (non-customized) purchases always go through Dawn's own product-form.js
    // untouched.
    function collectFormProperties(form) {
      const properties = {};
      new FormData(form).forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      });
      return properties;
    }

    // fetch() + JSON parse with a clear error (and console diagnostics) if the
    // response isn't actually JSON, instead of letting JSON.parse's cryptic
    // "Unexpected token '<'" surface directly to the customer.
    async function checkedJsonFetch(url, options) {
      const res = await fetch(url, options);
      const contentType = res.headers.get('content-type') || '';

      // Shopify's cart endpoints reply with `Content-Type: text/javascript`
      // (matching an `Accept: application/javascript` request) even though
      // the body is plain JSON text — only a genuinely non-JSON response
      // (e.g. an HTML error/password page) should be treated as a failure.
      if (!contentType.includes('json') && !contentType.includes('javascript')) {
        const bodyPreview = await res.text().catch(() => '');
        console.error('[DYO] Expected JSON from ' + url + ' but got "' + contentType + '" (status ' + res.status + '):', bodyPreview.slice(0, 300));
        throw new Error('Could not reach the cart. Please try again in a moment.');
      }

      return { ok: res.ok, status: res.status, data: await res.json() };
    }

    async function submitWithFee(form, feeVariantId) {
      const productFormEl = form.closest('product-form');
      const submitBtn = productFormEl && productFormEl.querySelector('[type="submit"]');
      const spinner = productFormEl && productFormEl.querySelector('.loading__spinner');
      const errorWrapper = productFormEl && productFormEl.querySelector('.product-form__error-message-wrapper');
      const errorMsgEl = errorWrapper && errorWrapper.querySelector('.product-form__error-message');
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      const variantIdInput = form.querySelector('[name="id"]');
      const quantityInput = form.querySelector('[name="quantity"]');
      const variantId = variantIdInput ? parseInt(variantIdInput.value, 10) : null;
      const quantity = quantityInput ? Math.max(1, parseInt(quantityInput.value, 10) || 1) : 1;

      if (submitBtn) submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn && submitBtn.classList.add('loading');
      spinner && spinner.classList.remove('hidden');
      if (errorWrapper) errorWrapper.hidden = true;

      const payload = {
        items: [
          { id: variantId, quantity, properties: collectFormProperties(form) },
          { id: feeVariantId, quantity },
        ],
      };

      try {
        // Step 1: add both line items. Kept as a plain `items:[]` add with no
        // `sections`/`sections_url` params — that combination alongside the
        // multi-item array isn't a documented/reliable pairing, and mixing
        // them is what previously produced an HTML (non-JSON) response here.
        const addRes = await checkedJsonFetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Matches Dawn's own product-form.js request exactly — Shopify's
            // cart endpoint uses the Accept header to decide whether to
            // return a JSON error or its full HTML "Cart Error" page on
            // failure (e.g. an invalid/placeholder fee variant ID), and
            // without this it was falling back to the HTML page.
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });

        if (!addRes.ok || addRes.data.status) {
          throw new Error(addRes.data.description || addRes.data.message || 'Could not add to cart.');
        }

        if (cart) {
          cart.setActiveElement && cart.setActiveElement(document.activeElement);

          // Step 2: separately fetch the rendered sections the cart UI needs,
          // via Shopify's general-purpose Section Rendering API — any
          // storefront URL with `?sections=id1,id2` returns a flat
          // `{ sectionId: html }` map directly. This is the same fallback
          // pattern the Ooop It reference implementation uses.
          const sectionIds = cart.getSectionsToRender().map((section) => section.id);
          const sectionsRes = await checkedJsonFetch(
            window.routes.cart_url + '?sections=' + encodeURIComponent(sectionIds.join(',')),
            { headers: { 'X-Requested-With': 'XMLHttpRequest' } }
          );

          // `.key` is what cart-notification.js reads; `.id` is what
          // cart-drawer.js reads — included together so this works whichever
          // cart type the merchant has configured.
          const renderData = { key: addRes.data.items[0].key, id: variantId, sections: sectionsRes.data };
          cart.renderContents(renderData);
          cart.classList.remove('is-empty');
        } else {
          window.location = window.routes.cart_url;
        }
      } catch (err) {
        console.error('[DYO] Combined cart add (product + decoration fee) failed:', err);
        if (errorWrapper) {
          errorWrapper.hidden = false;
          if (errorMsgEl) errorMsgEl.textContent = err.message || 'Something went wrong. Please try again.';
        }
      } finally {
        submitBtn && submitBtn.removeAttribute('aria-disabled');
        submitBtn && submitBtn.classList.remove('loading');
        spinner && spinner.classList.add('hidden');
      }
    }

    function setupFeeSubmitIntercept() {
      const form = document.getElementById(cfg.productFormId);
      const productFormEl = form && form.closest('product-form');
      if (!form || !productFormEl) return;

      // Capture phase on the ANCESTOR <product-form> element: this runs before
      // Dawn's own submit listener (attached directly to the <form> target
      // itself), so stopping propagation here prevents Dawn's default
      // single-item submit from also firing.
      productFormEl.addEventListener(
        'submit',
        (e) => {
          if (!hasLogo) return;

          const feeVariantId = feeVariantIdForCurrentDecoration();
          if (!feeVariantId) {
            console.warn(
              '[DYO] No fee variant configured for decoration type "' + decorationType + '" — adding product without a fee line item.'
            );
            return;
          }

          e.preventDefault();
          e.stopPropagation();
          submitWithFee(form, feeVariantId);
        },
        true
      );
    }

    setupFeeSubmitIntercept();

    // ─── Init ───────────────────────────────────────────────────────────────────
    canvas.addEventListener('mousedown', onDown);
    canvas.addEventListener('mousemove', onMove);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);

    canvas.addEventListener('touchstart', onDown, { passive: false });
    canvas.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);

    uploadBtn && uploadBtn.addEventListener('click', () => fileInput && fileInput.click());
    fileInput && fileInput.addEventListener('change', onFileSelect);

    loadProductImage();
  }

  function init() {
    const configs = window.DyoUploaderConfigs || {};
    Object.keys(configs).forEach((blockId) => initUploader(configs[blockId]));
  }

  init();
})();
